import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import ReactPlayer from "react-player";
import { Box, Typography, IconButton } from "@mui/material";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "../../store";
import { addMultiViewSlot, removeMultiViewSlot, toggleSlotMute, toggleSlotPlay, IMultiViewSlot } from "../../store/player/playerSlice";
import { useHlsMedia } from "../../hooks/Videos/useHlsStreaming";
import EventSelector from "./EventSelector";
import { Loader } from "../index";
import styles from "./VideoPlayer.module.css";

interface MultiViewSlotProps {
    slot: IMultiViewSlot;
    index: number;
    totalSlots: number;
}

const MultiViewSlot: React.FC<MultiViewSlotProps> = ({ slot, index, totalSlots }) => {
    const { t } = useTranslation("translations");
    const dispatch = useDispatch();
    const playerRef = useRef<ReactPlayer | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [loading, setLoading] = useState(true);

    const isLiveType = slot.type === "LIVE" || slot.type === "SPORTS" || !slot.type;
    const isMuted = slot.isMuted ?? (index > 0);
    const isPlaying = slot.isPlaying ?? true;

    const playerUrl = useMemo(() => {
        return slot.videoData?.url || undefined;
    }, [slot.videoData?.url]);

    const { handleHls, availableLanguages, availableSubtitles, selectedLanguage, selectedSubtitle } = useHlsMedia(playerRef);

    const initHls = useCallback(() => {
        if (playerUrl?.includes(".m3u8")) {
            handleHls(playerUrl, { isLiveContent: isLiveType });
        }
    }, [playerUrl, handleHls, isLiveType]);

    const handleReady = useCallback(() => {
        initHls();
        if (slot.currentTime && slot.currentTime > 0) {
            playerRef.current?.seekTo(slot.currentTime, "seconds");
        }
        setLoading(false);
    }, [initHls, slot.currentTime]);

    const handleRemove = () => {
        dispatch(removeMultiViewSlot(index));
    };

    const handleAddSlot = () => {
        dispatch(addMultiViewSlot());
    };

    const handleToggleMute = () => {
        dispatch(toggleSlotMute(index));
    };

    const handleTogglePlay = () => {
        dispatch(toggleSlotPlay(index));
    };

    const handleFullscreen = () => {
        if (containerRef.current && containerRef.current.requestFullscreen) {
            void containerRef.current.requestFullscreen();
        }
    };

    if (slot.isSelectingEvent) {
        return (
            <Box className={styles.slotCard}>
                <EventSelector slotIndex={index} />
            </Box>
        );
    }

    return (
        <Box ref={containerRef} className={styles.slotCard}>
            {/* Top Bar for Slot */}
            <Box className={styles.slotHeader}>
                <Box className={styles.slotBadge}>
                    <span className={styles.slotNumber}>{index + 1}</span>
                    <Typography variant="subtitle2" className={styles.slotTitle}>
                        {slot.title || `Player ${index + 1}`}
                    </Typography>
                </Box>
                <Box className={styles.slotHeaderRight}>
                    <span className={styles.slotLiveTag}>
                        <span className={styles.slotLiveDot} /> LIVE
                    </span>
                    {totalSlots < 4 && (
                        <IconButton
                            size="small"
                            className={styles.slotCloseBtn}
                            onClick={handleAddSlot}
                            title={t("Sports.addTranslation") || "Add translation"}
                            data-testid="slot-add-translation-btn"
                            sx={{ color: "#fff", background: "rgba(255, 255, 255, 0.15)", "&:hover": { background: "rgba(255, 255, 255, 0.3)" }, mr: 0.5 }}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    )}
                    <IconButton size="small" className={styles.slotCloseBtn} onClick={handleRemove}>
                        <CloseIcon fontSize="small" sx={{ color: "#fff" }} />
                    </IconButton>
                </Box>
            </Box>

            {/* Video Player Stream */}
            <Box className={styles.slotPlayerWrapper}>
                {playerUrl ? (
                    <ReactPlayer
                        data-testid="react-player"
                        key={`${slot.id ?? index}`}
                        ref={playerRef}
                        url={playerUrl}
                        playing={isPlaying}
                        loop={isLiveType}
                        width="100%"
                        height="100%"
                        muted={isMuted}
                        onReady={handleReady}
                        onLoad={() => setLoading(false)}
                        onError={() => setLoading(false)}
                    />
                ) : (
                    <Box className={styles.slotPlaceholder}>
                        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
                            No video stream available
                        </Typography>
                    </Box>
                )}

                {loading && <Loader className={styles.slotLoader} />}

                {/* Hover overlay controls */}
                <Box className={styles.slotOverlayControls}>
                    <IconButton className={styles.slotControlBtn} onClick={handleTogglePlay}>
                        {isPlaying ? <PauseIcon sx={{ color: "#fff" }} /> : <PlayArrowIcon sx={{ color: "#fff" }} />}
                    </IconButton>
                    <IconButton className={styles.slotControlBtn} onClick={handleToggleMute}>
                        {isMuted ? <VolumeOffIcon sx={{ color: "#fff" }} /> : <VolumeUpIcon sx={{ color: "#fff" }} />}
                    </IconButton>
                    {totalSlots < 4 && (
                        <IconButton className={styles.slotControlBtn} onClick={handleAddSlot} title={t("Sports.addTranslation") || "Add translation"}>
                            <AddIcon sx={{ color: "#fff" }} />
                        </IconButton>
                    )}
                    <IconButton className={styles.slotControlBtn} onClick={handleFullscreen}>
                        <FullscreenIcon sx={{ color: "#fff" }} />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
};

export default MultiViewSlot;
